import React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

export interface LatLngPoint {
  lat: number;
  lng: number;
}

export type GeometryKind = 'Point' | 'LineString' | 'Polygon';

interface Props {
  active: boolean;
  // Which single leaflet-draw tool to enable — area-type layers (Farm/Paddock)
  // draw a Polygon, a flight/tractor path draws a LineString, a trap placement
  // draws a Point (marker).
  shapeType: GeometryKind;
  // When shapeType is 'Polygon', drag a rectangle instead of the freehand
  // polygon tool — used for Image Overlay placement, where the footprint is
  // always an axis-aligned box, not an arbitrary shape. A Leaflet Rectangle
  // is a Polygon subclass, so onDrawn still reports it as 'Polygon'.
  useRectangleTool?: boolean;
  onDrawn: (geometryType: GeometryKind, points: LatLngPoint[]) => void;
}

// Thin imperative wrapper around leaflet-draw's polygon/polyline/marker tools —
// same pattern as MapCenterController elsewhere in this app for wrapping
// non-React Leaflet APIs. Deliberately uses the vanilla leaflet-draw plugin
// rather than a React wrapper package (react-leaflet-draw has a history of
// lagging behind react-leaflet/leaflet major version bumps).
const ZoneDrawControl: React.FC<Props> = ({ active, shapeType, useRectangleTool, onDrawn }) => {
  const map = useMap();
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const drawControlRef = useRef<L.Control.Draw | null>(null);

  // The CREATED listener below is registered once (effect depends only on
  // [map]) so it never re-subscribes — without this ref it would keep calling
  // the onDrawn closure from the very first render, which closes over stale
  // state (e.g. drawMode as it was at mount, always 'none') and silently no-ops.
  const onDrawnRef = useRef(onDrawn);
  useEffect(() => {
    onDrawnRef.current = onDrawn;
  }, [onDrawn]);

  useEffect(() => {
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    const handleCreated = (e: L.LeafletEvent) => {
      const layer = (e as unknown as { layer: L.Layer; layerType: string }).layer;
      const layerType = (e as unknown as { layerType: string }).layerType;
      drawnItems.clearLayers();
      drawnItems.addLayer(layer);

      if (layerType === 'marker') {
        const latlng = (layer as L.Marker).getLatLng();
        onDrawnRef.current('Point', [{ lat: latlng.lat, lng: latlng.lng }]);
      } else if (layerType === 'polyline') {
        const latlngs = (layer as L.Polyline).getLatLngs() as L.LatLng[];
        onDrawnRef.current('LineString', latlngs.map((p) => ({ lat: p.lat, lng: p.lng })));
      } else {
        const latlngs = ((layer as L.Polygon).getLatLngs()[0] as L.LatLng[]);
        onDrawnRef.current('Polygon', latlngs.map((p) => ({ lat: p.lat, lng: p.lng })));
      }
    };

    map.on((L as any).Draw.Event.CREATED, handleCreated);

    return () => {
      map.off((L as any).Draw.Event.CREATED, handleCreated);
      map.removeLayer(drawnItems);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (!active) {
      if (drawControlRef.current) {
        map.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
      return;
    }

    const drawPolygon = shapeType === 'Polygon' && !useRectangleTool;
    const drawRectangle = shapeType === 'Polygon' && !!useRectangleTool;
    const drawControl = new (L as any).Control.Draw({
      draw: {
        polygon: drawPolygon ? { allowIntersection: false, showArea: true } : false,
        rectangle: drawRectangle ? {} : false,
        polyline: shapeType === 'LineString' ? {} : false,
        marker: shapeType === 'Point' ? {} : false,
        circle: false,
        circlemarker: false,
      },
      edit: {
        featureGroup: drawnItemsRef.current,
        remove: true,
      },
    });
    map.addControl(drawControl);
    drawControlRef.current = drawControl;

    return () => {
      map.removeControl(drawControl);
      drawControlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, shapeType, useRectangleTool, map]);

  return null;
};

export default ZoneDrawControl;
